/**
 * Compensation-area breadcrumb origins. Built on the generic `from`-origin helpers in
 * `breadcrumbOrigin.ts`; this module only owns the compensation-specific matcher.
 *
 * When a user drills from a compensation cycle into an employee's Goals or Reviews, those
 * pages root their breadcrumb on the cycle they came from, carried in the `from` query param.
 * Per the breadcrumb-origin contract, display names (cycle name, employee name) come from the
 * destination page's own data, never from the URL — only the `from` href travels.
 */
import type { Breadcrumb } from "@performyard/py-components";

import { getSanitizedFrom } from "./breadcrumbOrigin";

export { appendCurrentLocationAsFrom } from "./breadcrumbOrigin";

export const COMPENSATION_LANDING_HREF = "/compensation";
const COMPENSATION_CYCLE_MATCH = "/compensation/cycles/";
const COMPENSATION_CYCLE_ID_PATTERN = /\/compensation\/cycles\/([^/?]+)/;

export type CompensationOrigin = {
	/** Sanitized internal `from` path back to the originating cycle (including its active tab). */
	from: string;
	/** The cycle id parsed out of `from`, used to re-fetch the cycle name on the destination page. */
	cycleId: string;
};

/**
 * Resolve a compensation-cycle origin from the destination page's search string. Returns null when
 * there is no recognized cycle `from` (e.g. a direct visit to My Goals / Reviews of Me).
 */
export const parseCompensationOrigin = (search: string): CompensationOrigin | null => {
	const from = getSanitizedFrom(search);
	if (!from || !from.includes(COMPENSATION_CYCLE_MATCH)) return null;
	const cycleId = from.match(COMPENSATION_CYCLE_ID_PATTERN)?.[1];
	if (!cycleId) return null;
	return { from, cycleId };
};

export type CompensationBreadcrumbLeaf = "Goals" | "Team Goals" | "Reviews";

type BuildCompensationBreadcrumbsArgs = {
	/** Href back to the originating cycle (the origin's `from`). */
	from: string;
	/** Name of the originating compensation cycle. */
	cycleName: string;
	/** Display name of the employee whose Goals/Reviews are being viewed. */
	employeeName: string;
	/** Employee id, used for the employee-dashboard crumb. */
	employeeId: string;
	/** The current page's leaf noun. */
	leafLabel: CompensationBreadcrumbLeaf;
};

/**
 * Build the four-crumb compensation chain for the My Goals and Reviews of Me pages:
 * `Compensation > {cycleName} > {employeeName} > {employeeName}'s {leafLabel}`. The leading crumbs
 * cross page families, so they hard-reload; the leaf is the current page (no link).
 */
export const buildCompensationBreadcrumbs = ({
	from,
	cycleName,
	employeeName,
	employeeId,
	leafLabel,
}: BuildCompensationBreadcrumbsArgs): Breadcrumb[] => [
	{ pageId: "compensation", name: "Compensation", href: COMPENSATION_LANDING_HREF, forceHardReload: true },
	{ pageId: "compensation-cycle", name: cycleName, href: from, forceHardReload: true },
	{
		pageId: "compensation-employee",
		name: employeeName,
		href: `/employee-page/${employeeId}`,
		forceHardReload: true,
	},
	{ pageId: "compensation-leaf", name: `${employeeName}'s ${leafLabel}`, href: "" },
];
